import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './StudentAnalytics.css';

const normalizeAnalyticsPayload = (raw) => {
  const weeks = raw?.weeks || [];
  const assignmentsRaw = raw?.assignments || [];
  const testsRaw = raw?.tests || [];

  const totalWeekItems = weeks.reduce((sum, w) => sum + Number(w?.total_items || 0), 0);
  const completedWeekItems = weeks.reduce((sum, w) => sum + Number(w?.completed_items || 0), 0);
  const progress = totalWeekItems > 0
    ? Math.round((completedWeekItems / totalWeekItems) * 100)
    : (weeks.length > 0 ? Math.round((weeks.filter(w => w?.status === 'completed').length / weeks.length) * 100) : 0);

  const assignments = assignmentsRaw.map((a) => {
    const sub = a?.submission;
    return {
      assignment_id: a?.assignment_id,
      submission_id: sub?.submission_id || null,
      title: a?.title || 'Untitled Assignment',
      due_date: a?.due_date || null,
      status: sub?.status || 'not_submitted',
      submitted_at: sub?.submitted_at || null,
      marks: sub?.marks,
      feedback: sub?.feedback || null,
      file_path: sub?.file_path || null,
      week_no: a?.week_no,
    };
  });

  const testAttempts = (testsRaw || [])
    .filter(t => t?.latest_attempt)
    .map((t) => ({
      test_id: t.test_id,
      test_title: t.title || 'Untitled Test',
      week_no: t.week_no,
      score: t.latest_attempt?.score ?? 0,
      total_marks: t.latest_attempt?.total_marks ?? 0,
      percentage: t.latest_attempt?.percentage ?? 0,
      status: t.latest_attempt?.status || 'failed',
      attempt_number: t.latest_attempt?.attempt_number ?? 1,
      submitted_at: t.latest_attempt?.submitted_at || null,
    }));

  const dates = [
    ...assignments.map(a => a.submitted_at).filter(Boolean),
    ...testAttempts.map(t => t.submitted_at).filter(Boolean),
  ].map(d => new Date(d)).filter(d => !Number.isNaN(d.getTime()));

  const weeklyScores = weeks.map((w) => {
    const total = Number(w?.total_items || 0);
    const completed = Number(w?.completed_items || 0);
    return total > 0 ? Math.round((completed / total) * 100) : (w?.status === 'completed' ? 100 : 0);
  });

  return {
    course_title: raw?.course?.course_name || '',
    progress,
    weeks_completed: weeks.filter(w => w?.status === 'completed').length,
    total_weeks: weeks.length,
    assignments_submitted: assignments.filter(a => a.status !== 'not_submitted').length,
    assignments_evaluated: assignments.filter(a => a.status === 'evaluated').length,
    tests_passed: testAttempts.filter(t => t.status === 'passed').length,
    tests_failed: testAttempts.filter(t => t.status !== 'passed').length,
    last_activity_date: dates.length ? new Date(Math.max(...dates.map(d => d.getTime()))).toISOString() : null,
    total_hours: 0,
    weekly_scores: weeklyScores,
    weeks_detail: weeks.map((w) => ({
      week_number: w.week_number,
      title: w.title,
      status: w.status,
      completed_date: null,
    })),
    assignments,
    test_attempts: testAttempts,
  };
};

const StudentAnalytics = ({ courseId, studentId, studentName, facultyId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [evalForms, setEvalForms] = useState({});
  const [actionMessage, setActionMessage] = useState(null);

  useEffect(() => {
    loadStudentAnalytics();
  }, [studentId, courseId]);

  const loadStudentAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/faculty/${facultyId}/student/${studentId}/course/${courseId}`
      );
      setAnalytics(normalizeAnalyticsPayload(response.data || {}));
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateEvalForm = (submissionId, field, value) => {
    setEvalForms((prev) => ({
      ...prev,
      [submissionId]: {
        ...(prev[submissionId] || {}),
        [field]: value,
      },
    }));
  };

  const handleEvaluateAssignment = async (assignment) => {
    return handleAssignmentDecision(assignment, 'approve');
  };

  const handleAssignmentDecision = async (assignment, decision) => {
    const submissionId = assignment?.submission_id;
    if (!submissionId) return;

    const form = evalForms[submissionId] || {};
    const marksValue = Number(form.marks);
    if (decision === 'approve' && !Number.isFinite(marksValue)) {
      setActionMessage({ type: 'error', text: 'Please enter valid marks before approving.' });
      return;
    }
    if (decision === 'reject' && !(form.feedback || '').trim()) {
      setActionMessage({ type: 'error', text: 'Please provide a rejection reason.' });
      return;
    }

    try {
      setActionMessage(null);
      updateEvalForm(submissionId, 'saving', true);
      await axios.post(`/api/faculty/${facultyId}/assignments/evaluate`, {
        submission_id: submissionId,
        decision,
        marks: decision === 'approve' ? marksValue : null,
        feedback: (form.feedback || '').trim(),
      });
      setActionMessage({
        type: 'success',
        text: decision === 'approve' ? 'Assignment approved successfully.' : 'Assignment rejected successfully.'
      });
      await loadStudentAnalytics();
    } catch (error) {
      const msg = error?.response?.data?.error || 'Failed to evaluate assignment.';
      setActionMessage({ type: 'error', text: msg });
    } finally {
      updateEvalForm(submissionId, 'saving', false);
    }
  };

  if (loading) return <div className="analytics-loader">Loading student analytics...</div>;

  if (!analytics) {
    return <div className="analytics-error">Failed to load analytics</div>;
  }

  return (
    <div className="student-analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div className="student-info">
          <div className="student-avatar">{studentName?.charAt(0)}</div>
          <div>
            <h2>{studentName}</h2>
            <p>Student ID: {studentId}</p>
          </div>
        </div>
        <div className="course-info">
          <span className="course-name">{analytics.course_title}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="analytics-tabs">
        <button
          className={`tab ${activeSection === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSection('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`tab ${activeSection === 'weeks' ? 'active' : ''}`}
          onClick={() => setActiveSection('weeks')}
        >
          📅 Weeks
        </button>
        <button
          className={`tab ${activeSection === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveSection('assignments')}
        >
          📝 Assignments
        </button>
        <button
          className={`tab ${activeSection === 'tests' ? 'active' : ''}`}
          onClick={() => setActiveSection('tests')}
        >
          🧪 Tests
        </button>
      </div>

      {/* Content */}
      <div className="analytics-content">
        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="section-content">
            <div className="stats-grid">
              {/* Progress Card */}
              <div className="stat-card">
                <div className="stat-header">
                  <h3>Overall Progress</h3>
                </div>
                <div className="progress-circle">
                  <svg viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" className="progress-bg" />
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      className="progress-ring"
                      style={{
                        strokeDasharray: `${(analytics.progress || 0) * 2.828} 282.8`
                      }}
                    />
                  </svg>
                  <div className="progress-text">
                    <span className="percentage">{analytics.progress || 0}%</span>
                    <span className="label">Complete</span>
                  </div>
                </div>
              </div>

              {/* Weeks Completed */}
              <div className="stat-card">
                <div className="stat-header">
                  <h3>Weeks Progress</h3>
                </div>
                <div className="stat-value-large">
                  {analytics.weeks_completed || 0}
                </div>
                <div className="stat-label">
                  of {analytics.total_weeks || 12} weeks
                </div>
              </div>

              {/* Assignments */}
              <div className="stat-card">
                <div className="stat-header">
                  <h3>Assignments</h3>
                </div>
                <div className="stat-breakdown">
                  <div className="breakdown-item">
                    <span className="status submitted">
                      {analytics.assignments_submitted || 0}
                    </span>
                    <span className="label">Submitted</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="status evaluated">
                      {analytics.assignments_evaluated || 0}
                    </span>
                    <span className="label">Evaluated</span>
                  </div>
                </div>
              </div>

              {/* Tests */}
              <div className="stat-card">
                <div className="stat-header">
                  <h3>Tests</h3>
                </div>
                <div className="stat-breakdown">
                  <div className="breakdown-item">
                    <span className="status passed">
                      {analytics.tests_passed || 0}
                    </span>
                    <span className="label">Passed</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="status failed">
                      {analytics.tests_failed || 0}
                    </span>
                    <span className="label">Failed</span>
                  </div>
                </div>
              </div>

              {/* Last Active */}
              <div className="stat-card">
                <div className="stat-header">
                  <h3>Last Activity</h3>
                </div>
                <div className="last-active">
                  {analytics.last_activity_date
                    ? new Date(analytics.last_activity_date).toLocaleDateString()
                    : 'No activity'}
                </div>
              </div>

              {/* Time Spent */}
              <div className="stat-card">
                <div className="stat-header">
                  <h3>Time on Course</h3>
                </div>
                <div className="time-spent">
                  {analytics.total_hours || 0} hours
                </div>
              </div>
            </div>

            {/* Performance Chart */}
            {analytics.weekly_scores && (
              <div className="chart-card">
                <h3>Weekly Performance</h3>
                <div className="chart-container">
                  {analytics.weekly_scores.map((score, idx) => (
                    <div key={idx} className="chart-bar">
                      <div className="bar-container">
                        <div
                          className="bar"
                          style={{
                            height: `${(score / 100) * 200}px`,
                            backgroundColor: score >= 50 ? '#16a34a' : '#dc2626'
                          }}
                        />
                        <div className="bar-label">W{idx + 1}</div>
                      </div>
                      <div className="bar-value">{score}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Weeks Section */}
        {activeSection === 'weeks' && (
          <div className="section-content">
            <div className="weeks-list">
              {analytics.weeks_detail?.map((week, idx) => (
                <div key={idx} className={`week-item week-${week.status}`}>
                  <div className="week-indicator">
                    {week.status === 'completed' && <span>✓</span>}
                    {week.status === 'current' && <span>▶</span>}
                    {week.status === 'locked' && <span>🔒</span>}
                  </div>
                  <div className="week-details">
                    <h4>Week {week.week_number}</h4>
                    <p>{week.title}</p>
                    {week.completed_date && (
                      <small>Completed: {new Date(week.completed_date).toLocaleDateString()}</small>
                    )}
                  </div>
                  <div className={`status-badge status-${week.status}`}>
                    {week.status === 'completed' && 'Completed'}
                    {week.status === 'current' && 'In Progress'}
                    {week.status === 'locked' && 'Locked'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assignments Section */}
        {activeSection === 'assignments' && (
          <div className="section-content">
            {actionMessage && (
              <div style={{ marginBottom: 12, color: actionMessage.type === 'error' ? '#b91c1c' : '#166534' }}>
                {actionMessage.text}
              </div>
            )}
            <div className="submissions-list">
              {analytics.assignments?.map((assignment, idx) => (
                <div key={idx} className={`submission-item status-${assignment.status}`}>
                  <div className="submission-header">
                    <h4>{assignment.title}</h4>
                    <span className={`status-badge status-${assignment.status}`}>
                      {assignment.status === 'evaluated' ? '✓ Approved' : (assignment.status === 'rejected' ? '✕ Rejected' : (assignment.status === 'submitted' ? '⏳ Submitted' : 'Not Submitted'))}
                    </span>
                  </div>

                  <div className="submission-details">
                    <div className="detail">
                      <span className="label">Submitted:</span>
                      <span className="value">
                        {assignment.submitted_at ? new Date(assignment.submitted_at).toLocaleString() : 'Not submitted'}
                      </span>
                    </div>

                    {assignment.due_date && (
                      <div className="detail">
                        <span className="label">Due Date:</span>
                        <span className="value">
                          {new Date(assignment.due_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {assignment.status === 'evaluated' && (
                      <>
                        <div className="detail">
                          <span className="label">Marks:</span>
                          <span className="value">{assignment.marks}</span>
                        </div>

                        {assignment.feedback && (
                          <div className="feedback">
                            <strong>Feedback:</strong>
                            <p>{assignment.feedback}</p>
                          </div>
                        )}
                      </>
                    )}

                    {assignment.status === 'rejected' && assignment.feedback && (
                      <div className="feedback">
                        <strong>Rejection Reason:</strong>
                        <p>{assignment.feedback}</p>
                      </div>
                    )}
                  </div>

                  {assignment.status === 'submitted' && assignment.submission_id && (
                    <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                      <div className="detail" style={{ display: 'grid', gap: 6 }}>
                        <label style={{ fontWeight: 600 }}>Marks</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={evalForms[assignment.submission_id]?.marks ?? ''}
                          onChange={(e) => updateEvalForm(assignment.submission_id, 'marks', e.target.value)}
                          placeholder="Enter marks"
                        />
                      </div>
                      <div className="detail" style={{ display: 'grid', gap: 6 }}>
                        <label style={{ fontWeight: 600 }}>Feedback</label>
                        <textarea
                          rows={3}
                          value={evalForms[assignment.submission_id]?.feedback ?? ''}
                          onChange={(e) => updateEvalForm(assignment.submission_id, 'feedback', e.target.value)}
                          placeholder="Enter feedback"
                        />
                      </div>
                      <button
                        className="btn-download-small"
                        type="button"
                        onClick={() => handleEvaluateAssignment(assignment)}
                        disabled={Boolean(evalForms[assignment.submission_id]?.saving)}
                        style={{ width: 'fit-content' }}
                      >
                        {evalForms[assignment.submission_id]?.saving ? 'Saving...' : 'Approve'}
                      </button>
                      <button
                        className="btn-download-small"
                        type="button"
                        onClick={() => handleAssignmentDecision(assignment, 'reject')}
                        disabled={Boolean(evalForms[assignment.submission_id]?.saving)}
                        style={{ width: 'fit-content', background: '#dc2626' }}
                      >
                        {evalForms[assignment.submission_id]?.saving ? 'Saving...' : 'Reject'}
                      </button>
                    </div>
                  )}

                  {assignment.file_path && (
                    <a
                      href={`/files/download/${assignment.file_path}`}
                      className="btn-download-small"
                    >
                      📥 Download Submission
                    </a>
                  )}
                </div>
              ))}

              {!analytics.assignments?.length && (
                <div className="empty-message">No assignments submitted</div>
              )}
            </div>
          </div>
        )}

        {/* Tests Section */}
        {activeSection === 'tests' && (
          <div className="section-content">
            <div className="tests-list">
              {analytics.test_attempts?.map((attempt, idx) => (
                <div key={idx} className={`test-item test-${attempt.status}`}>
                  <div className="test-header">
                    <h4>{attempt.test_title}</h4>
                    <span className={`result-badge result-${attempt.status}`}>
                      {attempt.status === 'passed' ? '✓ PASSED' : '✕ FAILED'}
                    </span>
                  </div>

                  <div className="test-stats">
                    <div className="stat">
                      <span className="label">Score</span>
                      <span className="value">{attempt.score}/{attempt.total_marks}</span>
                    </div>
                    <div className="stat">
                      <span className="label">Percentage</span>
                      <span className="value">{attempt.percentage}%</span>
                    </div>
                    <div className="stat">
                      <span className="label">Attempt</span>
                      <span className="value">#{attempt.attempt_number}</span>
                    </div>
                    <div className="stat">
                      <span className="label">Date</span>
                      <span className="value">
                        {new Date(attempt.submitted_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {!analytics.test_attempts?.length && (
                <div className="empty-message">No tests attempted</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentAnalytics;
